import { useState, useEffect, useMemo } from 'react';
import {
  ChakraBox,
  FAIconName,
  TagProps,
  useToastMessage,
} from '@equip.health/ui';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { CarePlanQolGoalResponse } from '~/lib/types/carePlan/carePlans';
import { useUpdateQoLGoalMutation } from '~/store/services/carePlan';
import { useStickyExpandAllLabel } from '~/lib/hooks/carePlan/useStickyExpandAllLabel';
import { CarePlanQoLGoalRow } from './CarePlanQoLGoalRow';
import { CarePlanSectionHeader } from './CarePlanSectionHeader';
import { getCarePlanGoalTagDropdownOptions } from './CarePlanGoalTagDropdownOptions';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { CarePlanEvents } from '~/analytics/carePlanEvents';

type Props = {
  qolGoals: CarePlanQolGoalResponse[];
  treatmentEpisodeId: string;
  carePlanId: string;
  statusDropdownOptions?: TagProps[];
  onAddDescriptionClick: (qolGoal: CarePlanQolGoalResponse) => void;
  onEditQoLGoalClick: (qolGoal: CarePlanQolGoalResponse) => void;
  onRemoveQoLGoalClick: (qolGoalId: string) => void;
};

export const ActiveCarePlanQoLGoalsSection = ({
  qolGoals,
  treatmentEpisodeId,
  carePlanId,
  statusDropdownOptions,
  onAddDescriptionClick,
  onEditQoLGoalClick,
  onRemoveQoLGoalClick,
}: Props) => {
  const { carePlanM4MedPsychProblems: isMedPsychProblemsEnabled } =
    useFlags<LDFlags>();
  const [expandedGoals, setExpandedGoals] = useState<Set<string>>(new Set());
  const [updateQoLGoal, { error }] = useUpdateQoLGoalMutation();
  const { createToast } = useToastMessage();
  const { trackEvent } = useAnalytics();

  const resolvedStatusDropdownOptions = useMemo(
    () =>
      statusDropdownOptions ??
      getCarePlanGoalTagDropdownOptions(isMedPsychProblemsEnabled),
    [statusDropdownOptions, isMedPsychProblemsEnabled],
  );

  useEffect(() => {
    if (error) {
      createToast({
        id: 'qol-goal-card-update-error',
        icon: FAIconName.exclamationTriangleRegular,
        text: "An error occurred updating the goal's status. Please try again later.",
        variant: 'critical',
      });
    }
  }, [error, createToast]);

  const toggleGoal = (goalId: string) => {
    setExpandedGoals((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(goalId)) {
        newSet.delete(goalId);
      } else {
        newSet.add(goalId);
      }
      return newSet;
    });
  };

  const showsCollapseAll = useStickyExpandAllLabel(qolGoals, expandedGoals);

  const handleExpandAll = () => {
    if (showsCollapseAll) {
      trackEvent(CarePlanEvents.clickOnCollapseAllQolGoalsButton);
      setExpandedGoals(new Set());
    } else {
      trackEvent(CarePlanEvents.clickOnExpandAllQolGoalsButton);
      setExpandedGoals(new Set(qolGoals.map((g) => g.id)));
    }
  };

  if (qolGoals.length === 0) {
    return null;
  }

  return (
    <ChakraBox paddingTop="30px" marginBottom="12px">
      <CarePlanSectionHeader
        id="qol-goals-section"
        title="Quality of life goals"
        count={qolGoals.length}
        expandAllLabel={showsCollapseAll ? 'Collapse all' : 'Expand all'}
        onExpandAllClick={handleExpandAll}
      />
      <ChakraBox>
        {qolGoals.map((qolGoal) => (
          <CarePlanQoLGoalRow
            key={qolGoal.id}
            qolGoal={qolGoal}
            isExpanded={expandedGoals.has(qolGoal.id)}
            carePlanId={carePlanId}
            statusDropdownOptions={resolvedStatusDropdownOptions}
            onToggle={toggleGoal}
            onAddDescriptionClick={onAddDescriptionClick}
            onEditQoLGoalClick={onEditQoLGoalClick}
            onRemoveQoLGoalClick={onRemoveQoLGoalClick}
            onStatusChange={(goalId, statusCode, description) => {
              updateQoLGoal({
                goalId,
                treatmentEpisodeId,
                body: {
                  description,
                  statusCode,
                  title: qolGoal.title,
                },
              });
            }}
          />
        ))}
      </ChakraBox>
    </ChakraBox>
  );
};
